import React, { useEffect, useState, useRef, useCallback } from 'react';

// Pure grouping helpers (no external deps)
const groupShapesIntoRows = (shapes, threshold) => {
  const sorted = [...shapes].sort((a, b) => a.y - b.y);
  const rows = [];
  sorted.forEach(s => {
    const row = rows.find(r => Math.abs(r.y - s.y) <= threshold);
    if (row) row.shapes.push(s);
    else rows.push({ y: s.y, shapes: [s] });
  });
  return rows;
};

const groupShapesIntoCols = (shapes, threshold) => {
  const sorted = [...shapes].sort((a, b) => a.x - b.x);
  const cols = [];
  sorted.forEach(s => {
    const col = cols.find(c => Math.abs(c.x - s.x) <= threshold);
    if (col) col.shapes.push(s);
    else cols.push({ x: s.x, shapes: [s] });
  });
  return cols;
};

/**
 * BendLayer � absolutely-positioned SVG overlay over the Pixi canvas.
 * orientation="vertical"   ? bends each ROW along a bezier (handles move in Y)
 * orientation="horizontal" ? bends each COL along a bezier (handles move in X)
 *
 * Props:
 *   shapes, selectedIds  � current shape data
 *   orientation          � "vertical" | "horizontal"
 *   zoom, panX, panY     � current viewport transform from PixiEditorSimple
 *   threshold            � row/col grouping tolerance (default 12.5)
 *   onApplyBend(updated) � called with the array of repositioned shapes on drag-end
 */
const BendLayer = ({
  orientation = 'vertical',
  selectedIds,
  shapes,
  threshold = 12.5,
  zoom,
  panX,
  panY,
  onApplyBend,
}) => {
  const [masterCurve, setMasterCurve] = useState(null);
  const [frozenGroups, setFrozenGroups] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Refs that mirror mutable values so window listeners never close over stale state
  const masterCurveRef   = useRef(null);
  const frozenGroupsRef  = useRef([]);
  const zoomRef          = useRef(zoom);
  const panXRef          = useRef(panX);   // eslint-disable-line no-unused-vars
  const panYRef          = useRef(panY);   // eslint-disable-line no-unused-vars

  useEffect(() => { masterCurveRef.current  = masterCurve;   }, [masterCurve]);
  useEffect(() => { frozenGroupsRef.current = frozenGroups;  }, [frozenGroups]);
  useEffect(() => { zoomRef.current         = zoom;          }, [zoom]);
  useEffect(() => { panXRef.current         = panX;          }, [panX]);
  useEffect(() => { panYRef.current         = panY;          }, [panY]);

  const majorKey = orientation === 'vertical' ? 'y' : 'x';
  const crossKey = orientation === 'vertical' ? 'x' : 'y';
  const groupFn  = orientation === 'vertical' ? groupShapesIntoRows : groupShapesIntoCols;

  // -- Initialise bezier anchor points whenever the selection changes ------
  useEffect(() => {
    const sel = selectedIds
      .map(id => shapes.find(s => s.id === id))
      .filter(Boolean);

    const groups = groupFn(sel, threshold);
    setFrozenGroups(groups);
    frozenGroupsRef.current = groups;

    if (!groups.length) {
      setMasterCurve(null);
      masterCurveRef.current = null;
      return;
    }

    // Derive start / end from the first group's cross-axis spread
    const coords = groups[0].shapes.map(s => s[crossKey]).sort((a, b) => a - b);
    const startC = coords[0];
    const endC   = coords[coords.length - 1];
    const midC   = (startC + endC) / 2;

    // masterCurve stores *offsets* from each group's major-axis position
    const curve = {
      start:   orientation === 'vertical' ? { x: startC, y: 0 } : { x: 0, y: startC },
      control: orientation === 'vertical' ? { x: midC,   y: 0 } : { x: 0, y: midC   },
      end:     orientation === 'vertical' ? { x: endC,   y: 0 } : { x: 0, y: endC   },
    };
    setMasterCurve(curve);
    masterCurveRef.current = curve;
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Coordinate helpers ---------------------------------------------------
  const toSvg = (sx, sy) => ({ x: sx * zoom + panX, y: sy * zoom + panY });

  const getAbsBezier = useCallback((group, mc) => {
    if (!mc) return null;
    if (orientation === 'vertical') {
      return {
        start:   { x: mc.start.x,   y: group.y + mc.start.y   },
        control: { x: mc.control.x, y: group.y + mc.control.y },
        end:     { x: mc.end.x,     y: group.y + mc.end.y     },
      };
    }
    return {
      start:   { x: group.x + mc.start.x,   y: mc.start.y   },
      control: { x: group.x + mc.control.x, y: mc.control.y },
      end:     { x: group.x + mc.end.x,     y: mc.end.y     },
    };
  }, [orientation]);

  // -- Apply quadratic bezier to all groups and commit ----------------------
  const applyBend = useCallback(() => {
    const mc     = masterCurveRef.current;
    const groups = frozenGroupsRef.current;
    if (!mc || !groups.length || !onApplyBend) return;

    const allUpdated = groups.flatMap(group => {
      const abs   = getAbsBezier(group, mc);
      if (!abs) return [];
      const count = group.shapes.length;
      if (count < 2) return group.shapes.map(s => ({ ...s }));

      const sorted = [...group.shapes].sort((a, b) => a[crossKey] - b[crossKey]);

      return sorted.map((shape, i) => {
        const t  = i / (count - 1);
        const u  = 1 - t;
        const x  = u*u*abs.start.x   + 2*u*t*abs.control.x   + t*t*abs.end.x;
        const y  = u*u*abs.start.y   + 2*u*t*abs.control.y   + t*t*abs.end.y;
        const dx = 2*u*(abs.control.x - abs.start.x) + 2*t*(abs.end.x - abs.control.x);
        const dy = 2*u*(abs.control.y - abs.start.y) + 2*t*(abs.end.y - abs.control.y);
        const rotation = Math.atan2(dy, dx) * (180 / Math.PI);
        return { ...shape, x, y, rotation };
      });
    });

    if (allUpdated.length) onApplyBend(allUpdated);
  }, [getAbsBezier, crossKey, onApplyBend]);

  // -- Drag handles (window listeners avoid stale closure bugs) ------------
  const dragRef = useRef(null);

  const handleHandleMouseDown = useCallback((pt, e) => {
    e.stopPropagation();
    e.preventDefault();

    const mc     = masterCurveRef.current;
    const groups = frozenGroupsRef.current;
    if (!mc || !groups.length) return;

    const baseMajor = groups[0][majorKey];
    const initStageX = orientation === 'vertical' ? mc[pt].x              : baseMajor + mc[pt].x;
    const initStageY = orientation === 'vertical' ? baseMajor + mc[pt].y  : mc[pt].y;

    dragRef.current = { pt, startMouseX: e.clientX, startMouseY: e.clientY, initStageX, initStageY, baseMajor };

    setIsDragging(true);
    document.body.style.cursor = 'grabbing';

    const onMove = (moveE) => {
      if (!dragRef.current) return;
      const z = zoomRef.current;
      const { pt: dpt, startMouseX, startMouseY, initStageX: isx, initStageY: isy, baseMajor: bm } = dragRef.current;

      const newStageX = isx + (moveE.clientX - startMouseX) / z;
      const newStageY = isy + (moveE.clientY - startMouseY) / z;
      const newPt = orientation === 'vertical'
        ? { x: newStageX,       y: newStageY - bm }
        : { x: newStageX - bm, y: newStageY      };

      const updated = { ...masterCurveRef.current, [dpt]: newPt };
      masterCurveRef.current = updated;
      setMasterCurve({ ...updated });
    };

    const onUp = () => {
      applyBend();
      dragRef.current = null;
      setIsDragging(false);
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [orientation, majorKey, applyBend]);

  // -- Render ---------------------------------------------------------------
  if (!masterCurve || !frozenGroups.length) return null;

  const firstGroup = frozenGroups[0];

  return (
    <svg
      style={{
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: '100%',
        overflow: 'visible', pointerEvents: 'none', zIndex: 10,
      }}
    >
      {/* Bezier guide curves for every row/col */}
      {frozenGroups.map((group, i) => {
        const abs = getAbsBezier(group, masterCurve);
        if (!abs) return null;
        const s  = toSvg(abs.start.x,   abs.start.y);
        const c  = toSvg(abs.control.x, abs.control.y);
        const en = toSvg(abs.end.x,     abs.end.y);
        return (
          <path
            key={`bend-${orientation}-curve-${i}`}
            d={`M ${s.x} ${s.y} Q ${c.x} ${c.y} ${en.x} ${en.y}`}
            fill="none"
            stroke="rgba(120,120,120,0.7)"
            strokeWidth={1.5}
            strokeDasharray="3 6"
            strokeLinecap="round"
          />
        );
      })}

      {/* Draggable handles anchored on the first row/col */}
      {['start', 'control', 'end'].map(pt => {
        const abs = getAbsBezier(firstGroup, masterCurve);
        if (!abs) return null;
        const pos   = toSvg(abs[pt].x, abs[pt].y);
        const color = pt === 'control'
          ? (orientation === 'vertical' ? '#f90' : '#38bdf8')
          : '#888';
        return (
          <circle
            key={`bend-${orientation}-handle-${pt}`}
            cx={pos.x} cy={pos.y} r={8}
            fill={color} stroke="white" strokeWidth={2}
            style={{ cursor: isDragging ? 'grabbing' : 'grab', pointerEvents: 'all' }}
            onMouseDown={(e) => handleHandleMouseDown(pt, e)}
          />
        );
      })}
    </svg>
  );
};

export default BendLayer;
